import React from "react";
import type { AlertImageUploadField, AlertTheme, FlowEditorNode } from "./types";
import { alertIconShapes, alertLayoutPresets } from "./constants";
import { normalizeAlertLayoutPreset } from "./constants";
import { AlertImageUploadControl, ColorInput, RangeInput, SelectInput, TextInput, ToggleRow } from "./FormControls";

type ImageUploadState = {
  nodeId: string;
  field: AlertImageUploadField;
  progress: number;
  error: string;
} | null;

type AlertNodeSettingsProps = {
  node: FlowEditorNode;
  imageUploadState: ImageUploadState;
  applyAlertLayoutPreset: (nodeId: string, preset: any) => void;
  clearImageUploadState: (nodeId: string, field: AlertImageUploadField) => void;
  updateNodeData: (nodeId: string, patch: Partial<FlowEditorNode["data"]>) => void;
  updateNodeSetting: (nodeId: string, key: string, value: any) => void;
  uploadNodeAlertImage: (node: FlowEditorNode, file: File, field: AlertImageUploadField) => void;
};

export function AlertNodeSettings({
  node,
  imageUploadState,
  applyAlertLayoutPreset,
  clearImageUploadState,
  updateNodeData,
  updateNodeSetting,
  uploadNodeAlertImage,
}: AlertNodeSettingsProps) {
  const settings = node.data.settings || {};
  const displaySeconds = Math.round(
      (Number(settings.displayMs) || 8000) / 1000,
    );
  const selectedTheme = String(settings.theme || "sticker") as AlertTheme;
  const selectedLayoutPreset = normalizeAlertLayoutPreset(
      settings.layoutPreset || settings.layout,
      selectedTheme,
    );
  const iconSize = Number(settings.iconSize);
  const iconUploadState =
      imageUploadState?.nodeId === node.id &&
      imageUploadState.field === "iconUrl"
        ? imageUploadState
        : null;

  return (
      <>
        <div className="grid grid-cols-1 gap-3 rounded-lg border border-[#dbe4ef] bg-[#f8fafc] p-3">
          <div className="text-xs font-black text-[#344054]">
            Preset layout
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {alertLayoutPresets.map((preset) => {
              const Icon = preset.icon;
              const isActive = selectedLayoutPreset === preset.key;

              return (
                <button
                  key={preset.key}
                  type="button"
                  className={`flex min-h-[72px] items-center gap-3 rounded-lg border p-3 text-left transition ${
                    isActive
                      ? "border-[#168feb] bg-[#eef7ff] text-[#168feb] shadow-[0_0_0_2px_rgba(22,143,235,0.12)]"
                      : "border-[#dbe4ef] bg-white text-[#475467] hover:border-[#98bfe4] hover:text-[#168feb]"
                  }`}
                  onClick={() => applyAlertLayoutPreset(node.id, preset.key)}
                >
                  <span className="grid h-9 w-9 flex-none place-items-center rounded-lg bg-[#f4f7fb]">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-black">
                      {preset.title}
                    </span>
                    <span className="block truncate text-xs font-medium text-[#667085]">
                      {preset.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 rounded-lg border border-[#dbe4ef] bg-[#f8fafc] p-3">
          <div className="text-xs font-black text-[#344054]">Alert icon</div>
          <SelectInput
            label="Icon shape"
            value={String(settings.iconShape || "none")}
            options={alertIconShapes}
            onChange={(value) =>
              updateNodeSetting(node.id, "iconShape", value)
            }
          />
          <AlertImageUploadControl
            label="Icon image"
            urlLabel="Icon URL"
            value={String(settings.iconUrl || settings.imageUrl || "")}
            inputId={`alert-icon-upload-${node.id}`}
            isUploading={Boolean(iconUploadState && !iconUploadState.error)}
            progress={iconUploadState?.progress || 0}
            error={iconUploadState?.error || ""}
            onUpload={(file) => uploadNodeAlertImage(node, file, "iconUrl")}
            onUrlChange={(value) => {
              clearImageUploadState(node.id, "iconUrl");
              updateNodeSetting(node.id, "iconMode", "image");
              updateNodeSetting(node.id, "showIcon", true);
              updateNodeSetting(node.id, "iconUrl", value);
              updateNodeSetting(node.id, "imageUrl", value);
              updateNodeData(node.id, { imageUrl: value });
            }}
            onClear={() => {
              clearImageUploadState(node.id, "iconUrl");
              updateNodeSetting(node.id, "iconMode", "image");
              updateNodeSetting(node.id, "showIcon", false);
              updateNodeSetting(node.id, "iconUrl", "");
              updateNodeSetting(node.id, "imageUrl", "");
              updateNodeData(node.id, { imageUrl: "" });
            }}
          />
          <RangeInput
            label="Icon size"
            value={Number.isFinite(iconSize) ? iconSize : 84}
            min={32}
            max={160}
            onChange={(value) =>
              updateNodeSetting(node.id, "iconSize", value)
            }
          />
        </div>

        <TextInput
          label="Template"
          value={String(settings.messageTemplate || node.data.text || "")}
          onChange={(value) => {
            updateNodeSetting(node.id, "messageTemplate", value);
            updateNodeData(node.id, { text: value });
          }}
        />
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <TextInput
            label="Min amount"
            type="number"
            value={String(settings.minAmount || 0)}
            onChange={(value) =>
              updateNodeSetting(node.id, "minAmount", Number(value || 0))
            }
          />
          <TextInput
            label="Display seconds"
            type="number"
            value={String(displaySeconds)}
            onChange={(value) =>
              updateNodeSetting(
                node.id,
                "displayMs",
                Math.max(Number(value || 0), 0) * 1000,
              )
            }
          />
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <SelectInput
            label="Animation"
            value={String(settings.animation || "pop")}
            options={[
              { value: "pop", label: "Pop" },
              { value: "slide", label: "Slide" },
              { value: "fade", label: "Fade" },
              { value: "bounce", label: "Bounce" },
            ]}
            onChange={(value) =>
              updateNodeSetting(node.id, "animation", value)
            }
          />
          <ColorInput
            label="Text color"
            value={String(settings.textColor || "#FFFFFF")}
            onChange={(value) =>
              updateNodeSetting(node.id, "textColor", value)
            }
          />
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <ToggleRow
            label="Donor"
            description="Show name"
            checked={settings.showDonorName !== false}
            onChange={(checked) =>
              updateNodeSetting(node.id, "showDonorName", checked)
            }
          />
          <ToggleRow
            label="Amount"
            description="Show money"
            checked={settings.showAmount !== false}
            onChange={(checked) =>
              updateNodeSetting(node.id, "showAmount", checked)
            }
          />
          <ToggleRow
            label="Message"
            description="Show text"
            checked={settings.showMessage !== false}
            onChange={(checked) =>
              updateNodeSetting(node.id, "showMessage", checked)
            }
          />
        </div>
        <ToggleRow
          label="Border glow"
          description="Highlight alert edge"
          checked={settings.showBorderGlow !== false}
          onChange={(checked) =>
            updateNodeSetting(node.id, "showBorderGlow", checked)
          }
        />
      </>
    );
}
